import { ReactNode, useMemo } from 'react';
import { Chain, ChainID } from '@ankr.com/chains-list';

// TODO: move to some common place https://ankrnetwork.atlassian.net/browse/MRPC-3911
import {
  getCodeEnterprise,
  getCodeMrpc,
} from 'modules/common/components/GetStartedSection/components/Snippets/utils/getCode';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { usePrivateChainItem } from 'domains/chains/screens/ChainPage/PrivateChainItemQuery/components/PrivateChainItem/hooks/usePrivateChainItem';
import { useTechnology } from 'modules/common/components/GetStartedSection/components/ConnectionSnippet/hooks/useTechnology';
import { useAuth } from 'modules/auth/hooks/useAuth';

export const getIsHiddenMainnet = (projectChain: Chain) => {
  if (
    projectChain.id === ChainID.AVALANCHE ||
    projectChain.id === ChainID.HORIZEN ||
    projectChain.id === ChainID.TENET ||
    projectChain.id === ChainID.SEI ||
    projectChain.id === ChainID.SECRET ||
    projectChain.id === ChainID.FLARE ||
    projectChain.id === ChainID.ZKVERIFY ||
    projectChain.id === ChainID.TAC ||
    projectChain.id === ChainID.KAVA ||
    projectChain.id === ChainID.STELLAR ||
    projectChain.id === ChainID.BTC ||
    projectChain.id === ChainID.TON ||
    projectChain.id === ChainID.NERVOS ||
    projectChain.id === ChainID.TRON ||
    projectChain.id === ChainID.FUEL ||
    projectChain.id === ChainID.MOVEMENT_MAINNET ||
    projectChain.id === ChainID.CORN ||
    projectChain.beacons?.length ||
    projectChain.opnodes?.length
  ) {
    return false;
  }

  return projectChain.paths?.length === 0;
};

export interface UseProjectChainDetailsParams {
  networksButton?: ReactNode;
  projectChain: Chain;
  isCompactView?: boolean;
  shouldMergeTendermintGroups?: boolean;
  shouldMergeTonGroups?: boolean;
  shouldMergeZkVerifyGroups?: boolean;
  shouldMergeTacTendermintGroups?: boolean;
  isChainSwitcherBlockingIgnored?: boolean;
  shouldExpandTestnets?: boolean;
  onOpenUpgradeModal?: () => void;
}

export const useProjectChainDetails = ({
  isChainSwitcherBlockingIgnored,
  isCompactView,
  networksButton,
  onOpenUpgradeModal,
  projectChain,
  shouldExpandTestnets,
  shouldMergeTacTendermintGroups,
  shouldMergeTendermintGroups,
  shouldMergeTonGroups,
  shouldMergeZkVerifyGroups,
}: UseProjectChainDetailsParams) => {
  const {
    chain: privateChain,
    chainProtocolContext,
    chainSubType,
    chainSubTypeTab,
    chainSubTypeTabs,
    chainType,
    chainTypeTab,
    chainTypeTabs,
    group,
    groupID,
    groupTab,
    groupTabs,
    groups,
    headerContent,
    selectGroup,
  } = usePrivateChainItem({
    additionalSelector: networksButton,
    chain: projectChain,
    isGroupSelectorAutoWidth: true,
    isHiddenMainnet: getIsHiddenMainnet(projectChain),
    isCompactView,
    shouldMergeTacTendermintGroups,
    shouldMergeTendermintGroups,
    shouldMergeTonGroups,
    shouldMergeZkVerifyGroups,
    isChainSwitcherBlockingIgnored,
    shouldExpandTestnets,
    onOpenUpgradeModal,
  });

  const [technology, setTechnology] = useTechnology();

  const { isLoggedIn } = useAuth();

  const { isEnterprise, isPremium } = useAccountStatus();

  const isGroupPublic = group.chains.every(chain => chain.isPublic);

  const [httpCode, wssCode] = useMemo(
    () =>
      isEnterprise
        ? getCodeEnterprise(technology, group)
        : getCodeMrpc({
            shouldAddPrefix: !isGroupPublic && !isLoggedIn,
            technology,
            urls: group.urls,
          }),
    [group, technology, isEnterprise, isGroupPublic, isLoggedIn],
  );

  return {
    chainProtocolContext,
    headerContent,
    httpCode,
    privateChain,
    setTechnology,
    technology,
    wssCode: isPremium ? wssCode : undefined,
    chainSubTypeTab,
    chainSubTypeTabs,
    chainTypeTab,
    chainTypeTabs,
    groupID,
    groupTab,
    groupTabs,
    groups,
    selectGroup,
    chainSubType,
    chainType,
    group,
  };
};
